import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Loader2, CheckCircle2, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import PhotoAttach from '../../pages/reviewShield/PhotoAttach';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const TX = {
    en: {
        checkIn: 'Guest Check-In', room: 'Room', questionOf: 'Question {i} of {n}',
        back: 'Back', next: 'Next', submit: 'Submit',
        comments: 'Additional Comments',
        commentsPrompt: 'Do you have any comments or suggestions to share? (optional)',
        commentsPlaceholder: 'Enter your comments here…',
        thankYou: 'Thank you for your feedback!',
        thankYouSub: 'We appreciate you taking the time to share your experience.',
        photoHelper: 'Share a photo to help us better understand your experience. Examples: cleanliness concerns, room or property issues, maintenance, or amenities.',
    },
    fr: {
        checkIn: 'Enregistrement des invités', room: 'Chambre', questionOf: 'Question {i} sur {n}',
        back: 'Retour', next: 'Suivant', submit: 'Soumettre',
        comments: 'Commentaires supplémentaires',
        commentsPrompt: 'Avez-vous des commentaires ou suggestions à partager ? (facultatif)',
        commentsPlaceholder: 'Écrivez vos commentaires ici…',
        thankYou: 'Merci pour vos commentaires !',
        thankYouSub: "Nous vous remercions d'avoir pris le temps de partager votre expérience.",
        photoHelper: 'Partagez une photo pour nous aider à mieux comprendre votre expérience. Exemples\u00a0: problèmes de propreté, problèmes de chambre ou d\u2019établissement, entretien ou commodités.',
    },
    es: {
        checkIn: 'Registro de huéspedes', room: 'Habitación', questionOf: 'Pregunta {i} de {n}',
        back: 'Atrás', next: 'Siguiente', submit: 'Enviar',
        comments: 'Comentarios adicionales',
        commentsPrompt: '¿Tiene algún comentario o sugerencia para compartir? (opcional)',
        commentsPlaceholder: 'Escriba sus comentarios aquí…',
        thankYou: '¡Gracias por sus comentarios!',
        thankYouSub: 'Le agradecemos que se haya tomado el tiempo de compartir su experiencia.',
        photoHelper: 'Comparta una foto para ayudarnos a comprender mejor su experiencia. Ejemplos: problemas de limpieza, problemas de la habitación o de la propiedad, mantenimiento o comodidades.',
    },
};

export const RoomFeedbackGuestFlow = ({ config, cardId, lang }) => {
    const tx = TX[lang] || TX.en;
    const questions = (config?.rf_questions || []).filter(q => q.enabled !== false);
    const [step, setStep] = useState(0);
    const [ratings, setRatings] = useState({});
    const [comment, setComment] = useState('');
    const [photos, setPhotos] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const advanceTimer = useRef(null);
    const { i18n } = useTranslation();

    useEffect(() => () => clearTimeout(advanceTimer.current), []);

    // Keep PhotoAttach labels in the guest's chosen language
    useEffect(() => {
        if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
    }, [lang, i18n]);

    useEffect(() => {
        fetch(`${API}/api/hotel/${cardId}/start`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
        }).then(r => r.ok ? r.json() : null).then(d => d && setSessionId(d.session_id)).catch(() => {});
    }, [cardId]);

    const q = questions[step];
    const isLast = step === questions.length - 1;
    const currentRating = q ? (ratings[q.id] || 0) : 0;
    const pct = questions.length ? Math.round(((step + 1) / questions.length) * 100) : 0;
    const qText = q ? (q[`text_${lang}`] || q.text_en || '') : '';
    const roomLabel = config?.room_number
        ? `${tx.room} ${config.room_number}`
        : (config?.touchpoint_name || config?.location_name || '');

    // Tap a star → auto-advance to the next question (no Next button).
    // The last question stays put so the guest can add comments and Submit.
    const setStar = (star) => {
        setRatings(prev => ({ ...prev, [q.id]: star }));
        if (!isLast) {
            clearTimeout(advanceTimer.current);
            advanceTimer.current = setTimeout(() => setStep(step + 1), 350);
        }
    };

    const goBack = () => {
        clearTimeout(advanceTimer.current);
        setStep(s => Math.max(0, s - 1));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const answers = questions.map(qu => ({
                question_id: qu.id,
                question_text: qu.text_en || qu[`text_${lang}`] || '',
                rating: ratings[qu.id],
            })).filter(a => a.rating);
            if (photos.length > 0) {
                // Review Shield parity: multipart submission with photos
                const fd = new FormData();
                fd.append('payload', JSON.stringify({ session_id: sessionId, answers, comment, language: lang }));
                photos.forEach(p => fd.append('photos', p.file, p.file.name));
                await fetch(`${API}/api/hotel/${cardId}/room-feedback-with-photos`, { method: 'POST', body: fd });
            } else {
                await fetch(`${API}/api/hotel/${cardId}/room-feedback`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sessionId, answers, comment, language: lang }),
                });
            }
            setDone(true);
        } catch (e) { /* retry allowed */ } finally { setSubmitting(false); }
    };

    if (done) {
        return (
            <div className="space-y-6 animate-in fade-in duration-500 text-center" data-testid="rf-thankyou-screen">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
                <h2 className="text-2xl font-bold text-white">{tx.thankYou}</h2>
                <p className="text-slate-300 text-sm">{tx.thankYouSub}</p>
            </div>
        );
    }

    if (!questions.length) {
        return <p className="text-slate-400 text-center" data-testid="rf-no-questions">This experience is not configured yet.</p>;
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500" data-testid="rf-guest-flow">
            {/* Hotel + room context */}
            <div className="text-center">
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1" data-testid="rf-hotel-name">{config?.hotel_name}</h1>
                <p className="text-slate-400 text-sm" data-testid="rf-context-line">
                    {[config?.location_name, roomLabel !== config?.location_name ? roomLabel : null, tx.checkIn].filter(Boolean).join(' • ')}
                </p>
            </div>

            {/* Progress */}
            <div data-testid="rf-progress">
                <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                    <span data-testid="rf-progress-label">{tx.questionOf.replace('{i}', step + 1).replace('{n}', questions.length)}</span>
                    <span data-testid="rf-progress-pct">{pct}%</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
            </div>

            {/* Question */}
            <p className="text-white text-lg sm:text-xl font-medium text-center min-h-[3.5rem] flex items-center justify-center" data-testid="rf-question-text">
                {qText}
            </p>

            {/* Stars */}
            <div className="flex justify-center gap-2" data-testid="rf-star-rating">
                {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} onClick={() => setStar(s)}
                        className={`p-2.5 rounded-full transition-all ${s <= currentRating ? 'bg-amber-500/20 scale-105' : 'bg-white/5 hover:bg-white/10'}`}
                        data-testid={`rf-star-${s}`}>
                        <Star className="w-8 h-8 sm:w-9 sm:h-9"
                            fill={s <= currentRating ? '#FBBF24' : 'transparent'}
                            stroke={s <= currentRating ? '#FBBF24' : '#64748b'} strokeWidth={1.5} />
                    </button>
                ))}
            </div>

            {/* Comments — on the last question, like the reference flow */}
            {isLast && (
                <div className="space-y-2" data-testid="rf-comments-section">
                    <p className="text-white text-sm font-medium flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-amber-400" />{tx.comments}
                    </p>
                    <p className="text-slate-400 text-xs">{tx.commentsPrompt}</p>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder={tx.commentsPlaceholder}
                        rows={3}
                        className="w-full rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                        data-testid="rf-comment-input"
                    />
                    <PhotoAttach photos={photos} onChange={setPhotos} helperText={tx.photoHelper} />
                </div>
            )}

            {/* Back / Submit — advancing is automatic on star tap */}
            <div className="flex justify-between items-center pt-2">
                <Button variant="outline" onClick={goBack}
                    disabled={step === 0}
                    className="border-white/20 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white h-11 px-6"
                    data-testid="rf-back-btn">
                    {tx.back}
                </Button>
                {isLast ? (
                    <Button onClick={handleSubmit} disabled={!currentRating || submitting}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-semibold h-11 px-6"
                        data-testid="rf-submit-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : tx.submit}
                    </Button>
                ) : (
                    <span className="text-xs text-slate-500" data-testid="rf-autoadvance-hint" />
                )}
            </div>
        </div>
    );
};
